import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import PublicLayout from './PublicLayout';
import config from '../../config';

const InputField = ({ name, label, type = 'text', placeholder, delay, required = true, value, onChange, focusedField, onFocus, onBlur, mounted, hasError }) => (
  <div
    className={`transition-all duration-500 ease-out ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
    style={{ transitionDelay: `${delay}s` }}
  >
    <label 
      className={`block text-sm font-medium mb-2 transition-colors duration-300 ${hasError ? 'text-red-400' : focusedField === name ? 'text-primary-500' : 'text-palegreen-70'}`}
    >
      {label}
      {required && <span className="text-red-400 ml-1">*</span>}
    </label>
    <input
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      onFocus={onFocus}
      onBlur={onBlur}
      placeholder={placeholder}
      required={required}
      className={`w-full px-4 py-3 rounded-xl text-base outline-none transition-all duration-300 bg-bg-forest-dark text-text-primary placeholder:text-palegreen-50 ${
        hasError
          ? 'border border-red-500/50 shadow-[0_0_10px_rgba(239,68,68,0.2)]'
          : focusedField === name 
            ? 'border border-success-50 shadow-glow-sm' 
            : 'border border-success-15'
      }`}
    />
    {hasError && (
      <p className="text-red-400 text-xs mt-1">{label} is required</p>
    )}
  </div>
);

InputField.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  type: PropTypes.string,
  placeholder: PropTypes.string,
  delay: PropTypes.number,
  required: PropTypes.bool,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  focusedField: PropTypes.string,
  onFocus: PropTypes.func.isRequired,
  onBlur: PropTypes.func.isRequired,
  mounted: PropTypes.bool.isRequired,
  hasError: PropTypes.bool,
};

const Contact = () => {
  const [mounted, setMounted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [focusedField, setFocusedField] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});

  useEffect(() => {
    setMounted(true);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError(null);
    // Clear error for this field when user starts typing
    if (fieldErrors[name]) {
      setFieldErrors(prev => ({ ...prev, [name]: false }));
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.name.trim()) errors.name = true;
    if (!formData.email.trim()) errors.email = true;
    if (!formData.subject.trim()) errors.subject = true;
    if (!formData.message.trim()) errors.message = true;
    
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      setError('Please fill in all required fields.');
      return;
    }
    
    setIsSubmitting(true);
    setError(null);
    
    try {
      const response = await fetch(`${config.api.baseURL}/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          subject: formData.subject || 'Contact from ForgeVault Website',
          message: formData.message,
        }),
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to send message');
      }
      
      setSubmitted(true);
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactMethods = [
    {
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
          <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
        </svg>
      ),
      title: 'Email',
      value: 'forgevault.app@gmail.com',
      desc: 'For inquiries, support, and feedback',
      link: 'mailto:forgevault.app@gmail.com',
    },
  ];

  return (
    <PublicLayout>
      <div className="px-6 lg:px-12 py-12 lg:py-16">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div 
            className={`text-center mb-12 transition-all duration-700 ease-out ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
            style={{ transitionDelay: '0.1s' }}
          >
            {/* Logo */}
            <div className="flex justify-center mb-8">
              <img 
                src="/logo.png"
                alt="ForgeVault Logo" 
                className="w-20 h-20 lg:w-24 lg:h-24 object-contain"
              />
            </div>

            <h1 className="text-3xl lg:text-5xl font-bold mb-6 leading-tight text-text-primary">
              Contact Us
            </h1>
            <p className="text-lg max-w-xl mx-auto leading-relaxed text-palegreen-70">
              Have questions or need help? We'd love to hear from you.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">
            {/* Contact Form */}
            <div 
              className={`lg:col-span-3 transition-all duration-700 ease-out ${mounted ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}
              style={{ transitionDelay: '0.2s' }}
            >
              <div className="p-6 lg:p-8 rounded-2xl bg-bg-forest-dark border border-success-10 backdrop-blur-lg">
                {submitted ? (
                  <div className="text-center py-10 animate-fadeIn">
                    <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6 bg-gradient-cta shadow-glow">
                      <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="#000" strokeWidth="2.5">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                      </svg>
                    </div>
                    <h3 className="text-xl font-bold mb-3 text-text-primary">
                      Message Sent!
                    </h3>
                    <p className="text-base mb-6 text-palegreen-70">
                      Thank you for reaching out! We've received your message and will get back to you within 24 hours.
                    </p>
                    <button
                      onClick={() => setSubmitted(false)}
                      className="px-6 py-3 rounded-xl text-sm font-medium transition-all duration-300 bg-success-10 border border-success-20 text-primary-500 hover:bg-success-20"
                    >
                      Send Another Message
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-5">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      <InputField
                        name="name"
                        label="Name"
                        placeholder="Your name"
                        delay={0.3}
                        value={formData.name}
                        onChange={handleChange}
                        focusedField={focusedField}
                        onFocus={() => setFocusedField('name')}
                        onBlur={() => setFocusedField(null)}
                        mounted={mounted}
                        hasError={fieldErrors.name}
                      />
                      <InputField
                        name="email"
                        label="Email"
                        type="email"
                        placeholder="you@example.com"
                        delay={0.35}
                        value={formData.email}
                        onChange={handleChange}
                        focusedField={focusedField}
                        onFocus={() => setFocusedField('email')}
                        onBlur={() => setFocusedField(null)}
                        mounted={mounted}
                        hasError={fieldErrors.email}
                      />
                    </div>
                    
                    <InputField
                      name="subject"
                      label="Subject"
                      placeholder="How can we help?"
                      delay={0.4}
                      value={formData.subject}
                      onChange={handleChange}
                      focusedField={focusedField}
                      onFocus={() => setFocusedField('subject')}
                      onBlur={() => setFocusedField(null)}
                      mounted={mounted}
                      hasError={fieldErrors.subject}
                    />
                    
                    <div
                      className={`transition-all duration-500 ease-out ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
                      style={{ transitionDelay: '0.45s' }}
                    >
                      <label 
                        className={`block text-sm font-medium mb-2 transition-colors duration-300 ${fieldErrors.message ? 'text-red-400' : focusedField === 'message' ? 'text-primary-500' : 'text-palegreen-70'}`}
                      >
                        Message<span className="text-red-400 ml-1">*</span>
                      </label>
                      <textarea
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        onFocus={() => setFocusedField('message')}
                        onBlur={() => setFocusedField(null)}
                        placeholder="Tell us more about your inquiry..."
                        rows={5}
                        required
                        className={`w-full px-4 py-3 rounded-xl text-base outline-none transition-all duration-300 resize-none bg-bg-forest-dark text-text-primary placeholder:text-palegreen-50 ${
                          fieldErrors.message
                            ? 'border border-red-500/50 shadow-[0_0_10px_rgba(239,68,68,0.2)]'
                            : focusedField === 'message' 
                              ? 'border border-success-50 shadow-glow-sm' 
                              : 'border border-success-15'
                        }`}
                      />
                      {fieldErrors.message && (
                        <p className="text-red-400 text-xs mt-1">Message is required</p>
                      )}
                    </div>

                    {error && (
                      <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
                        {error}
                      </div>
                    )}

                    <div
                      className={`transition-all duration-500 ease-out ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
                      style={{ transitionDelay: '0.5s' }}
                    >
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className={`w-full py-4 rounded-xl text-base font-semibold transition-all duration-300 relative overflow-hidden flex items-center justify-center gap-2 text-black ${
                          isSubmitting 
                            ? 'bg-success-20 cursor-not-allowed' 
                            : 'bg-gradient-cta shadow-glow hover:-translate-y-0.5 hover:shadow-glow-lg cursor-pointer'
                        }`}
                      >
                        {!isSubmitting && (
                          <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-shimmer" />
                        )}
                        {isSubmitting ? (
                          <>
                            <div className="w-5 h-5 border-2 border-black/20 border-t-black rounded-full animate-spin" />
                            <span className="relative z-10">Sending...</span>
                          </>
                        ) : (
                          <>
                            <span className="relative z-10">Send Message</span>
                            <svg className="w-5 h-5 relative z-10" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.27 20.876L5.999 12zm0 0h7.5" />
                            </svg>
                          </>
                        )}
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </div>

            {/* Contact Info */}
            <div 
              className={`lg:col-span-2 transition-all duration-700 ease-out ${mounted ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`}
              style={{ transitionDelay: '0.3s' }}
            >
              <div className="space-y-5">
                {contactMethods.map((method, idx) => (
                  <a
                    key={idx}
                    href={method.link || `mailto:${method.value}`}
                    className={`p-5 rounded-xl transition-all duration-300 group block bg-bg-dark-accent border border-success-10 no-underline hover:border-success-20 hover:-translate-y-1 ${
                      mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'
                    }`}
                    style={{ transitionDelay: `${0.4 + idx * 0.1}s` }}
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 transition-all duration-300 group-hover:scale-110 bg-gradient-forest-subtle border border-success-10 text-primary-500">
                        {method.icon}
                      </div>
                      <div>
                        <h3 className="font-semibold mb-1 transition-colors duration-300 group-hover:text-primary-300 text-text-primary">
                          {method.title}
                        </h3>
                        <p className="text-sm font-medium mb-1 text-primary-500">
                          {method.value}
                        </p>
                        <p className="text-sm text-palegreen-50">
                          {method.desc}
                        </p>
                      </div>
                    </div>
                  </a>
                ))}

                {/* Additional Info Card */}
                <div
                  className={`p-6 rounded-xl bg-gradient-forest-subtle border border-success-10 transition-all duration-500 ease-out ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'}`}
                  style={{ transitionDelay: '0.6s' }}
                >
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4 bg-gradient-cta text-black">
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <h3 className="font-semibold mb-2 text-text-primary">
                    Response Time
                  </h3>
                  <p className="text-sm leading-relaxed text-palegreen-60">
                    We typically respond within 24 hours on business days. 
                    For urgent matters, please include "Urgent" in your subject line.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        .animate-shimmer {
          animation: shimmer 2s infinite;
        }
      `}</style>
    </PublicLayout>
  );
};

export default Contact;
